/**
 * ENHANCED EventService - Properly parse new database format and sort comments
 */

/**
 * Parse existing remarks - handle new database format with proper sorting
 * @param {Object|Array|string} existingRemarks 
 * @returns {Array} Array of comment objects sorted by timestamp (latest first)
 */
export const parseExistingRemarks = (existingRemarks) => {
  if (!existingRemarks) {
    return [];
  }

  // If it's already an array (from frontend state), return it sorted
  if (Array.isArray(existingRemarks)) {
    return existingRemarks.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  }

  // Handle NEW database format: { user: "admin", comment: "REMARKS_HISTORY:..." }
  if (typeof existingRemarks === 'object' && existingRemarks.comment) {
    const comment = existingRemarks.comment;
    
    // Check if it's our structured history format
    if (comment.startsWith('REMARKS_HISTORY:')) {
      try {
        const historyData = comment.replace('REMARKS_HISTORY:', '');
        const parsed = JSON.parse(historyData);
        
        if (parsed && Array.isArray(parsed.comments)) {
          // Sort by timestamp - LATEST FIRST
          return parsed.comments.sort((a, b) => 
            new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
          );
        }
      } catch (error) {
        console.warn('⚠️ Failed to parse REMARKS_HISTORY:', error);
      }
    }
    
    // Single comment - convert to array format
    return [{
      user: existingRemarks.user || 'Unknown',
      comment: comment,
      timestamp: existingRemarks.timestamp || existingRemarks.createdAt || new Date().toISOString()
    }];
  }

  // Handle direct string format (fallback)
  if (typeof existingRemarks === 'string') {
    if (existingRemarks.startsWith('REMARKS_HISTORY:')) {
      try {
        const historyData = existingRemarks.replace('REMARKS_HISTORY:', '');
        const parsed = JSON.parse(historyData);
        
        if (parsed && Array.isArray(parsed.comments)) {
          // Sort by timestamp - LATEST FIRST
          return parsed.comments.sort((a, b) => 
            new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
          );
        }
      } catch (error) {
        console.warn('⚠️ Failed to parse string REMARKS_HISTORY:', error);
      }
    }
    
    // Treat as single comment
    return [{
      user: 'Unknown',
      comment: existingRemarks,
      timestamp: new Date().toISOString()
    }];
  }

  return [];
};

/**
 * Format comments for database storage
 * @param {Array} commentsArray 
 * @returns {string} Formatted comment string with metadata
 */
export const formatCommentsForStorage = (commentsArray) => {
  if (!Array.isArray(commentsArray) || commentsArray.length === 0) {
    return '';
  }

  // Ensure all comments have required fields and sort by timestamp
  const sanitizedComments = commentsArray
    .map(comment => ({
      user: comment.user || 'Unknown',
      comment: comment.comment || '',
      timestamp: comment.timestamp || new Date().toISOString()
    }))
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)); // Store in chronological order

  const historyData = {
    comments: sanitizedComments,
    totalComments: sanitizedComments.length,
    lastUpdated: new Date().toISOString(),
    version: '2.0'
  };

  return 'REMARKS_HISTORY:' + JSON.stringify(historyData);
};

/**
 * MAIN FUNCTION: Simple comment preservation using frontend state
 * @param {string} baseUrl 
 * @param {string} eventId 
 * @param {string|null} severity 
 * @param {Object|null} newRemark 
 * @param {Array} frontendComments - Current comments from frontend (MAIN SOURCE)
 * @returns {Promise} 
 */
export const updateEventPriority = async (baseUrl, eventId, severity, newRemark, frontendComments = []) => {
  if (!baseUrl || !eventId) {
    throw new Error('Missing required parameters');
  }

  const apiUrl = `${baseUrl.replace(/\/$/, '')}/api/va/event`;
  
  try {
    let finalRemark = null;
    
    if (newRemark && newRemark.user && newRemark.comment) {
      console.log('📝 ENHANCED: Adding comment to existing frontend comments...');
      console.log(`📚 Frontend has: ${frontendComments.length} existing comments`);
      
      // Use frontend comments as base (they're already correct and displayed)
      const existingComments = Array.isArray(frontendComments) ? [...frontendComments] : [];
      
      // Add new comment with timestamp
      const timestampedRemark = {
        user: newRemark.user,
        comment: newRemark.comment,
        timestamp: newRemark.timestamp || new Date().toISOString()
      };
      
      // Simple append
      const allComments = [...existingComments, timestampedRemark];
      
      console.log(`✅ ENHANCED: ${existingComments.length} + 1 = ${allComments.length} total comments`);
      
      // Format for database
      const formattedComment = formatCommentsForStorage(allComments);
      
      finalRemark = {
        user: newRemark.user,
        comment: formattedComment
      };
    }
    
    // Create request body
    const requestBody = {
      rowData: {
        eventId: eventId.toString()
      }
    };
    
    if (severity) {
      requestBody.severity = severity;
    }
    
    if (finalRemark) {
      requestBody.remarks = finalRemark;
    }
    
    console.log('🚀 Making API call with:', {
      eventId: requestBody.rowData.eventId,
      severity: requestBody.severity || 'unchanged',
      hasRemarks: !!requestBody.remarks,
      totalCommentsInRemarks: finalRemark ? JSON.parse(finalRemark.comment.replace('REMARKS_HISTORY:', '')).totalComments : 0
    });
    
    const response = await fetch(apiUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed: ${response.status} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log('✅ Success! Database updated with all comments preserved.');
    
    return result;
    
  } catch (error) {
    console.error('❌ Error updating event:', error);
    throw error;
  }
};

/**
 * Extract user-friendly comment history with proper formatting and sorting
 * @param {Object|Array|string} remarks 
 * @returns {Array} Array of user comments with formatted timestamps (latest first)
 */
export const extractCommentHistory = (remarks) => {
  const parsedComments = parseExistingRemarks(remarks);
  
  // Filter out system messages and return user-friendly format
  const userComments = parsedComments
    .filter(comment => {
      const commentText = comment.comment || '';
      return commentText && 
             !commentText.startsWith('EVENT_CLOSED_BY_USER:') &&
             !commentText.startsWith('EVENT_REOPENED_BY_USER:') &&
             !commentText.startsWith('REMARKS_HISTORY:');
    })
    .map((comment, index) => ({
      user: comment.user,
      comment: comment.comment,
      timestamp: comment.timestamp,
      formattedTime: comment.timestamp ? new Date(comment.timestamp).toLocaleString('en-IN', {
        year: 'numeric',
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'Asia/Kolkata'
      }) : 'Unknown time',
      commentNumber: index + 1
    }));

  // Already sorted by parseExistingRemarks (latest first)
  return userComments;
};

/**
 * NEW: Extract closure information if event is closed
 * @param {Object|Array|string} remarks 
 * @returns {Object|null} Closure information or null
 */
export const extractClosureInfo = (remarks) => {
  const parsedComments = parseExistingRemarks(remarks);
  
  // Find the most recent closure comment
  const closureComments = parsedComments.filter(comment => 
    comment.comment && comment.comment.startsWith('EVENT_CLOSED_BY_USER:')
  );
  
  if (closureComments.length === 0) return null;
  
  // Get the most recent closure (already sorted by timestamp)
  const latestClosure = closureComments[0];
  
  try {
    const closureData = latestClosure.comment.replace('EVENT_CLOSED_BY_USER:', '');
    const parsed = JSON.parse(closureData);
    
    return {
      closedBy: parsed.closedBy,
      closedAt: parsed.closedAt,
      priority: parsed.priority,
      finalComment: parsed.finalComment,
      formattedClosedAt: new Date(parsed.closedAt).toLocaleString('en-IN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit', 
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'Asia/Kolkata'
      })
    };
  } catch (error) {
    console.warn('Failed to parse closure data:', error);
    return {
      closedBy: latestClosure.user,
      closedAt: latestClosure.timestamp,
      priority: 'Unknown',
      finalComment: 'Event closed',
      formattedClosedAt: new Date(latestClosure.timestamp).toLocaleString()
    };
  }
};

/**
 * Simple event update for remarks only
 * @param {string} baseUrl 
 * @param {string} eventId 
 * @param {Object} remarks 
 * @param {Array} frontendComments 
 * @returns {Promise} 
 */
export const updateEventRemarks = async (baseUrl, eventId, remarks, frontendComments = []) => {
  return await updateEventPriority(baseUrl, eventId, null, remarks, frontendComments);
};

/**
 * Get event details (for compatibility with existing components)
 * @param {string} baseUrl 
 * @param {string} eventId 
 * @returns {Promise} 
 */
export const getEventDetails = async (baseUrl, eventId) => {
  if (!baseUrl || !eventId) {
    throw new Error('Missing event information required for fetching details');
  }
  
  try {
    // Try to get individual event details
    const individualEventUrl = `${baseUrl.replace(/\/$/, '')}/api/va/event?id=${eventId}`;
    
    const response = await fetch(individualEventUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    if (response.ok) {
      const eventData = await response.json();
      
      // Enhance with comment parsing
      return {
        data: {
          ...eventData,
          parsedComments: parseExistingRemarks(eventData.remarks),
          commentHistory: extractCommentHistory(eventData.remarks),
          closureInfo: extractClosureInfo(eventData.remarks)
        }
      };
    } else {
      console.warn('⚠️ Could not fetch event details via GET endpoint');
      return {
        data: {
          message: 'Event not found',
          eventId: eventId
        }
      };
    }
  } catch (error) {
    console.error('❌ Error fetching event details:', error);
    throw error;
  }
};

/**
 * Fetch events with enhanced comment parsing
 * @param {string} endpoint 
 * @param {Object} filters 
 * @returns {Promise} 
 */
export const fetchEvents = async (endpoint, filters) => {
  const apiUrl = `${endpoint.replace(/\/$/, '')}/api/va/events`;
  
  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(filters)
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const result = await response.json();
    
    // Enhanced event processing with comment parsing
    if (result.data && Array.isArray(result.data)) {
      result.data = result.data.map(event => {
        const parsedComments = parseExistingRemarks(event.remarks);
        const commentHistory = extractCommentHistory(event.remarks);
        const closureInfo = extractClosureInfo(event.remarks);
        
        return {
          ...event,
          parsedComments: parsedComments,
          commentHistory: commentHistory,
          closureInfo: closureInfo,
          totalComments: parsedComments.length,
          userComments: commentHistory.length
        };
      });
    }
    
    return result;
  } catch (error) {
    console.error('❌ Error fetching events:', error);
    throw error;
  }
};

/**
 * Get event statistics
 * @param {Array} events 
 * @returns {Object} Statistics object
 */
export const getEnhancedEventStatistics = (events) => {
  if (!Array.isArray(events)) {
    return { 
      total: 0, 
      open: 0, 
      closed: 0, 
      withComments: 0, 
      totalComments: 0,
      avgCommentsPerEvent: 0
    };
  }

  const total = events.length;
  let closed = 0;
  let withComments = 0;
  let totalComments = 0;

  events.forEach(event => {
    // Parse all remarks for analysis
    const allRemarks = parseExistingRemarks(event.remarks);
    
    // Check if event is closed
    const isClosed = allRemarks.some(remark => 
      remark.comment && remark.comment.startsWith('EVENT_CLOSED_BY_USER:')
    );
    
    if (isClosed) {
      closed++;
    }

    // Count user comments
    const userComments = extractCommentHistory(event.remarks);
    totalComments += userComments.length;
    
    if (userComments.length > 0) {
      withComments++;
    }
  });

  const open = total - closed;
  const avgCommentsPerEvent = total > 0 ? Math.round((totalComments / total) * 100) / 100 : 0;

  return { 
    total, 
    open, 
    closed, 
    withComments,
    totalComments,
    avgCommentsPerEvent,
    commentPercentage: total > 0 ? Math.round((withComments / total) * 100) : 0
  };
};